/**
 * custom-facets.js
 *
 * Defines the <custom-filter-active> web component which renders active filter
 * tag pills for metafield-driven collection filters.
 *
 * Why a custom element?
 * ---------------------
 * facets.js re-renders sections via AJAX on every filter change. When the
 * section HTML is replaced, connectedCallback fires automatically on the new
 * element, so the pills update without any extra event wiring.
 *
 * The element reads its config from data-config (JSON array of filter objects
 * matching the custom.filter_config metafield schema) and reads active values
 * directly from the URL via URLSearchParams.
 *
 * Active tag links use is="facet-remove" so facets.js intercepts clicks and
 * performs AJAX section updates (same as native filter remove links).
 *
 * Product Type ordering
 * ---------------------
 * Product Type is always the first active tag(s):
 *   - If data-native-pt-label is present, Product Type is served from the
 *     native Search & Discovery filter (filter.p.product_type) and is
 *     handled here before the metafield config loop.
 *   - If product_type is in the metafield config (no data-native-pt-label),
 *     the config sort puts it first naturally.
 */

if (!customElements.get('custom-filter-active')) {
  class CustomFilterActive extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      const config = this._parseConfig();
      const params = new URLSearchParams(window.location.search);
      const collectionUrl = this.dataset.collectionUrl || window.location.pathname;
      const nativePtLabel = this.dataset.nativePtLabel || '';

      const tags = [];

      // ── Product Type: native S&D path (filter.p.product_type) ──────────────
      // Only used when the PIM hasn't included product_type in filter_config.
      // data-native-pt-label is set by custom-facets-active.liquid in this case.
      if (nativePtLabel) {
        const ptValues = params.getAll('filter.p.product_type');
        for (const value of ptValues) {
          tags.push({
            label: this._escape(nativePtLabel) + ': ' + this._escape(value),
            removeUrl: this._buildRemoveUrl(params, 'filter.p.product_type', value),
          });
        }
      }

      // ── Custom metafield filters ────────────────────────────────────────────
      // product_type from config (if present) naturally sorts first; all others follow.
      const sorted = [
        ...config.filter((f) => f.key === 'product_type'),
        ...config.filter((f) => f.key !== 'product_type'),
      ];

      for (const filter of sorted) {
        const paramName = `filter.p.m.custom.${filter.key}`;

        if (filter.type === 'range') {
          const gteParam = `${paramName}.gte`;
          const lteParam = `${paramName}.lte`;
          const gteVal = params.get(gteParam);
          const lteVal = params.get(lteParam);

          if (gteVal !== null || lteVal !== null) {
            const from = gteVal !== null ? gteVal : '';
            const to = lteVal !== null ? lteVal : '';
            const label = `${this._escape(filter.label)}: ${this._escape(from)}–${this._escape(to)}`;
            tags.push({ label, removeUrl: this._buildRemoveRangeUrl(params, gteParam, lteParam) });
          }
        } else {
          const values = params.getAll(paramName);
          for (const value of values) {
            tags.push({
              label: this._escape(filter.label) + ': ' + this._escape(value),
              removeUrl: this._buildRemoveUrl(params, paramName, value),
            });
          }
        }
      }

      // ── Build HTML ──────────────────────────────────────────────────────────
      let html = '';
      for (const tag of tags) {
        html += `
          <a
            class="facet-remove btn btn--secondary flex relative"
            href="${tag.removeUrl}"
            is="facet-remove"
          >
            <div class="btn__text flex items-center justify-between gap-2">
              <div class="facet-title text-wrap flex items-center gap-2">${tag.label}</div>
              ${this._closeIcon()}
            </div>
          </a>`;
      }

      if (tags.length > 1) {
        html += `
          <a href="${collectionUrl}" class="facet-clear-all btn--link" is="facet-remove">
            ${window.theme?.strings?.clearAll || 'Clear all'}
          </a>`;
      }

      this.innerHTML = html;

      // ── Update all <facet-count> elements (drawer button + apply button) ───
      this._updateFacetCounts(tags.length);
    }

    // ─── URL helpers ──────────────────────────────────────────────────────────

    _buildRemoveUrl(params, paramName, valueToRemove) {
      const next = new URLSearchParams(params);
      const remaining = next.getAll(paramName).filter((v) => v !== valueToRemove);
      next.delete(paramName);
      remaining.forEach((v) => next.append(paramName, v));
      next.delete('page');
      return this._toUrl(next);
    }

    _buildRemoveRangeUrl(params, gteParam, lteParam) {
      const next = new URLSearchParams(params);
      next.delete(gteParam);
      next.delete(lteParam);
      next.delete('page');
      return this._toUrl(next);
    }

    _toUrl(params) {
      const qs = params.toString();
      return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    }

    // ─── UI helpers ───────────────────────────────────────────────────────────

    _updateFacetCounts(count) {
      document.querySelectorAll('facet-count').forEach((el) => {
        if (count === 0) {
          el.setAttribute('hidden', '');
          el.textContent = '(0)';
        } else {
          el.removeAttribute('hidden');
          el.textContent = `(${count})`;
        }
      });
    }

    _closeIcon() {
      // Matches the icon-close extra-small output from the theme's snippet
      return `<svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>`;
    }

    _parseConfig() {
      try {
        return JSON.parse(this.dataset.config || '[]');
      } catch {
        return [];
      }
    }

    _escape(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('custom-filter-active', CustomFilterActive);
}

/**
 * syncFilterState
 *
 * Reads the current URL params and, for every custom filter accordion:
 *   1. Restores checkbox / range-input checked/value state.
 *   2. Keeps the accordion open when it has active selections.
 *   3. Updates the ".facet-active-count" badge in the summary.
 *
 * Called on DOMContentLoaded (for initial page loads with filters in the URL)
 * and on collection:rerendered (after every AJAX section update).
 *
 * Background: facets.js replaces #FacetFiltersContainer innerHTML via
 * history.replaceState + renderFacetFiltersContainer. The server-rendered
 * HTML relies on request.parameters, which is unreliable in Shopify's section
 * rendering context for filter.p.m.custom.* params. Reading from URLSearchParams
 * after the URL has been updated is the reliable source of truth.
 */
function syncFilterState() {
  const params = new URLSearchParams(window.location.search);

  document.querySelectorAll('form[is="facet-form"] [data-filter]').forEach(function (filterEl) {
    if (filterEl.tagName !== 'DETAILS') return;

    let activeCount = 0;

    // ── Checkboxes (list / boolean / native product type) ──────────────────
    filterEl.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      const name = input.getAttribute('name');
      if (!name) return;
      if (name.startsWith('filter.p.m.custom.') || name === 'filter.p.product_type') {
        const isChecked = params.getAll(name).includes(input.value);
        input.checked = isChecked;
        if (isChecked) activeCount++;
      }
    });

    // ── Range number inputs ─────────────────────────────────────────────────
    let rangeActive = false;
    filterEl.querySelectorAll('input[type="number"]').forEach(function (input) {
      const name = input.getAttribute('name');
      if (!name) return;
      if (name.startsWith('filter.p.m.custom.') && (name.endsWith('.gte') || name.endsWith('.lte'))) {
        const val = params.get(name);
        input.value = val !== null ? val : '';
        if (val !== null && val !== '') rangeActive = true;
      }
    });
    if (rangeActive) activeCount += 1;

    // ── Keep accordion open when filters are active ─────────────────────────
    if (activeCount > 0) {
      filterEl.open = true;
    }

    // ── Update the count badge ──────────────────────────────────────────────
    const badge = filterEl.querySelector('.facet-active-count');
    if (badge) {
      if (activeCount > 0) {
        badge.textContent = '(' + activeCount + ')';
        badge.hidden = false;
      } else {
        badge.hidden = true;
        badge.textContent = '';
      }
    }
  });
}

function updateCustomFacetCount(count) {
  var el = document.getElementById('ProductCount');
  if (!el) return;
  var label;
  if (count === 1 && el.dataset.countTemplateOne) {
    label = el.dataset.countTemplateOne;
  } else if (el.dataset.countTemplatePlural) {
    label = el.dataset.countTemplatePlural.replace('__COUNT__', String(count));
  } else {
    label = String(count);
  }
  el.textContent = label;
}

function updateCustomFacetEmptyState(count, grid) {
  var empty = document.getElementById('CustomFacetEmptyState');
  if (empty) empty.hidden = count !== 0;
  grid.classList.toggle('custom-facet-hidden', count === 0);
}

function guardCustomFacetTruncation(renderedCount, grid) {
  var resultsCount = parseInt(grid.dataset.resultsCount || '0', 10);
  if (resultsCount && renderedCount < resultsCount) {
    console.warn(
      '[custom-facets] Client filtering is operating on a partial set: ' +
        renderedCount +
        ' of ' +
        resultsCount +
        ' products rendered (pagination cap of 250 reached). Some filter results may be missing.'
    );
  }
}

var CUSTOM_PREFIX = 'filter.p.m.custom.';

function getCustomFacetConfig() {
  var el = document.querySelector('custom-filter-active');
  if (!el) return [];
  try {
    return JSON.parse(el.dataset.config || '[]');
  } catch (e) {
    return [];
  }
}

function getAllProductFacets() {
  var grid = document.getElementById('ProductsList');
  if (!grid) return [];
  var out = [];
  grid.querySelectorAll('.f-column.product-column script.js-product-facets').forEach(function (s) {
    try {
      out.push(JSON.parse(s.textContent));
    } catch (e) {
      out.push(null);
    }
  });
  return out;
}

function updateFacetCounts() {
  var core = window.CustomFacetsCore;
  if (!core) return;
  var config = getCustomFacetConfig();
  var facets = getAllProductFacets();
  var active = core.parseActiveFilters(new URLSearchParams(window.location.search), config);
  // Contextual counts (given the shopper's current selections) and absolute
  // presence (with no filters). A value with 0 absolute presence is not backed
  // by any product in this collection — often a stale/mismatched value in the
  // PIM-written filter_values — so hide it rather than showing a permanent dead
  // option. A value present in the collection but 0 given the OTHER active
  // selections is a real dead-end: grey it (kept visible so the list is stable).
  var counts = core.computeFacetCounts(facets, active, config);
  var totals = core.computeFacetCounts(facets, { list: {}, range: {} }, config);

  document.querySelectorAll('form[is="facet-form"] input[type="checkbox"]').forEach(function (input) {
    var name = input.getAttribute('name') || '';
    if (name.indexOf(CUSTOM_PREFIX) !== 0) return;
    var key = name.slice(CUSTOM_PREFIX.length);
    if (!counts[key]) return; // range or unknown facet
    var norm = core.normalizeFacetValue(input.value);
    var contextual = counts[key][norm] || 0;
    var absolute = totals[key][norm] || 0;
    var li = input.closest('li');
    if (!li) return;

    // Absent from the collection entirely -> hide (keep it if it is somehow the
    // active selection, so it stays removable).
    var absent = absolute === 0 && !input.checked;
    li.classList.toggle('custom-facet-hidden', absent);
    if (absent) {
      li.classList.remove('custom-facet-value--empty');
      return;
    }

    var span = li.querySelector('[data-facet-count]');
    if (span) {
      span.textContent = '(' + contextual + ')';
      span.hidden = false;
    }
    var deadEnd = contextual === 0 && !input.checked;
    li.classList.toggle('custom-facet-value--empty', deadEnd);
    input.disabled = deadEnd;
  });

  // Hide any list/boolean facet accordion left with no available options (every
  // value absent from the collection) — e.g. a "Model" facet whose PIM-written
  // values match no product. Range facets (number inputs, no checkboxes) and the
  // native Product Type accordion (non-custom checkboxes) are left untouched.
  document.querySelectorAll('form[is="facet-form"] details[data-filter]').forEach(function (acc) {
    var boxes = acc.querySelectorAll('input[type="checkbox"][name^="' + CUSTOM_PREFIX + '"]');
    if (!boxes.length) return;
    var anyVisible = false;
    boxes.forEach(function (b) {
      var li = b.closest('li');
      if (li && !li.classList.contains('custom-facet-hidden')) anyVisible = true;
    });
    acc.classList.toggle('custom-facet-hidden', !anyVisible);
  });
}

/**
 * applyCustomFilters
 *
 * Hybrid client-side narrowing pass. Native Search & Discovery has already
 * filtered by the registered facets server-side; this hides the cards that
 * don't match the *unregistered* custom filters, reading selections from the
 * URL and each card's .js-product-facets island.
 *
 * Runs on initial load (shared/bookmarked links) and after every facets.js
 * section render (collection:rerendered).
 */
function applyCustomFilters() {
  var core = window.CustomFacetsCore;
  var grid = document.getElementById('ProductsList');
  if (!core || !grid) return;

  var config = [];
  var activeEl = document.querySelector('custom-filter-active');
  if (activeEl) {
    try {
      config = JSON.parse(activeEl.dataset.config || '[]');
    } catch (e) {
      config = [];
    }
  }

  var params = new URLSearchParams(window.location.search);
  var active = core.parseActiveFilters(params, config);
  var hasActive = Object.keys(active.list).length > 0 || Object.keys(active.range).length > 0;

  var columns = grid.querySelectorAll('.f-column.product-column');
  var visible = 0;

  columns.forEach(function (col) {
    var facets = null;
    var island = col.querySelector('script.js-product-facets');
    if (island) {
      try {
        facets = JSON.parse(island.textContent);
      } catch (e) {
        facets = null;
      }
    }
    var match = !hasActive || core.productMatchesFilters(facets, active);
    col.classList.toggle('custom-facet-hidden', !match);
    if (match) visible += 1;
  });

  guardCustomFacetTruncation(columns.length, grid);
  updateCustomFacetCount(visible);
  updateCustomFacetEmptyState(visible, grid);
  return visible;
}

function refreshCustomFacets() {
  syncFilterState();
  applyCustomFilters();
  document.querySelectorAll('custom-filter-active').forEach(function (el) {
    if (typeof el.render === 'function') el.render();
  });
  updateFacetCounts();
}

// Instant client-side filtering hides cards without a section re-render, so
// facets.js's usual scroll-into-view (beforeRenderSection) never fires. When the
// grid shrinks while the shopper is scrolled down they can be left below the
// results. Reposition to the top of the collection — but only scroll UP (when
// currently below the target), so it never jerks the page down when they are
// already near the top.
//
// The scroll is an INSTANT jump, done synchronously. The collection's top (its
// heading/toolbar, above the product grid) does not move when grid cards are
// hidden, so the target is stable even before the grid reflows, and the small
// target value stays valid after the grid shrinks. A *smooth* scroll would be
// cancelled mid-animation by the browser's scroll-position clamp as the grid
// shrinks — leaving the shopper stranded at the bottom (the very bug this fixes).
function scrollToCollectionTop() {
  var el = document.querySelector('.collection') || document.getElementById('ProductGridContainer');
  if (!el) return;
  var target = el.getBoundingClientRect().top + window.scrollY - 200;
  if (target < 0) target = 0;
  if (window.scrollY <= target) return; // already at/above the collection top
  window.scrollTo(0, target);
}

// Client-managed params are the only ones this script owns: the custom metafield
// filters (filter.p.m.custom.*) plus pagination. Everything else in the URL —
// sort_by, native Product Type (filter.p.product_type), and native price /
// availability / variant filters (filter.v.*) — is applied server-side and must
// be preserved verbatim, because CustomFacetsCore cannot reapply it.
function isClientManagedParam(key) {
  return key.indexOf(CUSTOM_PREFIX) === 0 || key === 'page' || key === 'section_id';
}

// Rebuild the URL after a client-side custom-filter change: keep every
// server-side parameter as-is and re-derive only the custom metafield filters
// from the form's current inputs.
function buildCustomFilterUrl(form) {
  var next = new URLSearchParams(window.location.search);
  var toDelete = [];
  next.forEach(function (value, key) {
    if (isClientManagedParam(key)) toDelete.push(key);
  });
  toDelete.forEach(function (key) {
    next.delete(key);
  });

  form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
    var name = input.getAttribute('name') || '';
    if (input.checked && name.indexOf(CUSTOM_PREFIX) === 0) next.append(name, input.value);
  });
  form.querySelectorAll('input[type="number"]').forEach(function (input) {
    var name = input.getAttribute('name') || '';
    if (name.indexOf(CUSTOM_PREFIX) === 0 && (name.slice(-4) === '.gte' || name.slice(-4) === '.lte')) {
      if (input.value !== '') next.set(name, input.value);
    }
  });

  var qs = next.toString();
  return qs ? window.location.pathname + '?' + qs : window.location.pathname;
}

function isCustomFilterInput(el) {
  if (!el || !el.getAttribute) return false;
  var name = el.getAttribute('name') || '';
  return name.indexOf(CUSTOM_PREFIX) === 0;
}

// Instant client-side filtering: intercept custom-filter interactions in the
// capture phase so facets.js's bubble-phase fetch handler never runs.
document.addEventListener(
  'change',
  function (e) {
    var target = e.target;
    if (!isCustomFilterInput(target)) return;
    var form = target.closest('form[is="facet-form"]');
    if (!form) return;
    e.stopPropagation(); // prevent facets.js change->fetch
    history.pushState({}, '', buildCustomFilterUrl(form));
    refreshCustomFacets();
    scrollToCollectionTop();
  },
  true
);

// NOTE: no submit interceptor. Custom-filter changes are stopped at the capture
// phase (above) so they never reach facets.js. Native Product Type changes DO
// dispatch facets.js's submit and must reach FacetForm.onFormSubmit to fetch
// server-side; the drawer "Apply" is type="button" (no submit), so there is no
// stray submit to block.

function serverFilterKey(search) {
  var pairs = [];
  new URLSearchParams(search).forEach(function (value, key) {
    if (key.indexOf("filter.") === 0 && key.indexOf(CUSTOM_PREFIX) !== 0) {
      pairs.push(key + "=" + value);
    }
  });
  return JSON.stringify(pairs.sort());
}

document.addEventListener(
  'click',
  function (e) {
    var link = e.target.closest && e.target.closest('a[is="facet-remove"]');
    if (!link) return;
    // Only intercept within the custom facet UI (active pills / custom empty-state clear).
    var inCustom =
      link.closest('#FacetFiltersActive') ||
      link.closest('#CustomFacetEmptyState') ||
      link.classList.contains('facet-clear-all');
    if (!inCustom) return;
    var href = link.getAttribute('href') || window.location.pathname;
    // If this removal changes any server-side filter (a Product Type pill, a
    // native price/availability/variant pill, or "Clear all" while any of those
    // is active), let facets.js handle it server-side — client-side can't restore
    // products the server filtered out.
    if (serverFilterKey(window.location.search) !== serverFilterKey(href.split('?')[1] || '')) {
      return; // fall through to facets.js FacetRemove -> AJAX fetch
    }
    e.preventDefault();
    e.stopPropagation();
    // A client-side clear only toggles card visibility; it does not re-sort the
    // DOM. "Clear all" and the empty-state clear use a bare collection URL that
    // drops sort_by, which would leave the URL claiming default order while the
    // grid stays in the previous sort order. Preserve the active sort_by so the
    // URL and the (already-sorted) displayed order stay consistent.
    var nextUrl = new URL(href, window.location.href);
    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort && !nextUrl.searchParams.has('sort_by')) {
      nextUrl.searchParams.set('sort_by', currentSort);
    }
    history.pushState({}, '', nextUrl.pathname + nextUrl.search);
    refreshCustomFacets();
    scrollToCollectionTop();
  },
  true
);

window.addEventListener('popstate', refreshCustomFacets);

// Ensure all collection products are in the DOM. Only fetches when the server
// rendered a partial set (a filtered deep-link where a registered S&D facet was
// applied). One unfiltered section fetch; never runs on the common unfiltered entry.
function hydrateAllProducts() {
  var grid = document.getElementById('ProductsList');
  if (!grid) return Promise.resolve();
  var all = parseInt(grid.dataset.resultsCountAll || '0', 10);
  var rendered = grid.querySelectorAll('.f-column.product-column').length;
  if (!all || rendered >= all) return Promise.resolve();
  // Over the 250 render cap we cannot load the full set in a single page. Bail
  // rather than fetching page 1 and replacing later pages with it (which would
  // silently drop products). Client filtering then operates on the rendered
  // page only; guardCustomFacetTruncation() surfaces the warning.
  if (all > 250) {
    console.warn(
      '[custom-facets] collection has ' +
        all +
        ' products (>250 render cap); skipping hydrate to avoid dropping products. ' +
        'Client filtering operates on the rendered page only.'
    );
    return Promise.resolve();
  }

  var form = document.querySelector('form[is="facet-form"]');
  var sectionId = form && form.getAttribute('data-section-id');
  if (!sectionId) return Promise.resolve();

  // Strip only the client-managed custom metafield filters (+ page) so the full
  // set is loaded for client-side filtering/counts. KEEP every server-side
  // filter — native Product Type, price/availability/variant (filter.v.*) — and
  // sort_by, because CustomFacetsCore cannot reapply those; they must stay
  // applied by the server through hydration.
  var params = new URLSearchParams(window.location.search);
  var keysToDelete = [];
  params.forEach(function (value, key) {
    if (isClientManagedParam(key)) keysToDelete.push(key);
  });
  keysToDelete.forEach(function (key) {
    params.delete(key);
  });
  params.set('section_id', sectionId);
  var url = window.location.pathname + '?' + params.toString();

  return fetch(url)
    .then(function (r) {
      return r.text();
    })
    .then(function (text) {
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var freshList = doc.getElementById('ProductsList');
      var liveList = document.getElementById('ProductsList');
      if (freshList && liveList) liveList.innerHTML = freshList.innerHTML;
    })
    .catch(function () {
      /* leave the partial set in place on failure */
    });
}

document.addEventListener('DOMContentLoaded', function () {
  hydrateAllProducts().then(refreshCustomFacets);
});
document.addEventListener('collection:rerendered', function () {
  hydrateAllProducts().then(refreshCustomFacets);
});
